import { useEffect, useRef, useState } from "react";
import { Search, X } from "lucide-react";
import { cn } from "@/lib/utils";

interface ExplanationSearchBarProps {
  value: string;
  onChange: (value: string) => void;
  matchCount: number;
  placeholder?: string;
  className?: string;
}

/**
 * Compact search field for filtering/highlighting inside an explanation.
 * Debounces input by ~80ms before propagating to the parent.
 */
export function ExplanationSearchBar({
  value,
  onChange,
  matchCount,
  placeholder = "Search in explanation…",
  className,
}: ExplanationSearchBarProps) {
  const [local, setLocal] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync external resets (e.g., tab/card change) into the local state.
  useEffect(() => {
    setLocal(value);
  }, [value]);

  // Debounced propagation.
  useEffect(() => {
    if (local === value) return;
    const t = window.setTimeout(() => onChange(local), 80);
    return () => window.clearTimeout(t);
  }, [local, value, onChange]);

  const hasQuery = local.length > 0;
  const isActive = local.trim().length >= 2;

  return (
    <div
      className={cn(
        "flex items-center gap-2 rounded-md border border-input bg-background/60",
        "px-2.5 py-1.5 focus-within:ring-2 focus-within:ring-ring",
        className
      )}
      role="search"
    >
      <Search className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden />
      <input
        ref={inputRef}
        type="text"
        inputMode="search"
        value={local}
        onChange={(e) => setLocal(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape" && hasQuery) {
            e.preventDefault();
            setLocal("");
            onChange("");
          }
        }}
        placeholder={placeholder}
        aria-label="Search in explanation"
        className={cn(
          "flex-1 min-w-0 bg-transparent text-sm outline-none",
          "placeholder:text-muted-foreground"
        )}
      />
      {isActive && (
        <span
          className={cn(
            "text-xs tabular-nums shrink-0",
            matchCount > 0 ? "text-foreground/80" : "text-muted-foreground"
          )}
          aria-live="polite"
        >
          {matchCount} {matchCount === 1 ? "match" : "matches"}
        </span>
      )}
      {hasQuery && (
        <button
          type="button"
          onClick={() => {
            setLocal("");
            onChange("");
            inputRef.current?.focus();
          }}
          aria-label="Clear search"
          className={cn(
            "shrink-0 inline-flex items-center justify-center rounded-sm",
            "h-5 w-5 text-muted-foreground hover:text-foreground hover:bg-muted/60",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          )}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}
